import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import axios from 'axios';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

type AssetKey = 'nps' | 'ppf' | 'epf' | 'gold' | 'lic' | 'real_estate' | 'fixed_deposit' | 'bank_account';

interface AssetSummary {
  total_value?: number;
  investment?: number;
  gain_loss?: number;
  return_percent?: number;
  xirr?: number | null;
  interest_rate?: number | null;
  monthly_contribution?: number | null;
  metadata?: Record<string, any>;
  has_asset?: boolean;
}

interface PortfolioSummary {
  total_investment: number;
  current_value: number;
  total_gain: number;
  gain_percent: number;
  mutual_funds_value: number;
  stocks_value: number;
}

interface WealthItem {
  key: string;
  label: string;
  category: string;
  value: number;
  investment: number;
  gain: number;
  rate: number;
  monthlyContribution: number;
  color: string;
}

interface RetirementProjectionPoint {
  year: number;
  yearLabel: string;
  projectedCorpus: number;
  requiredCorpus: number;
}

interface RetirementProjectionResult {
  data: RetirementProjectionPoint[];
  crossingYears: number | null;
  crossingCorpus: number | null;
}

const assetConfig: Record<AssetKey, Omit<WealthItem, 'key' | 'value' | 'investment' | 'gain' | 'rate' | 'monthlyContribution'> & { defaultRate: number }> = {
  nps: { label: 'National Pension System', category: 'Retirement', color: '#5b8def', defaultRate: 8.5 },
  ppf: { label: 'Public Provident Fund', category: 'Retirement', color: '#29a36a', defaultRate: 7.1 },
  epf: { label: 'Employee Provident Fund', category: 'Retirement', color: '#28b6d6', defaultRate: 8.25 },
  gold: { label: 'Gold Holdings', category: 'Real assets', color: '#d99a22', defaultRate: 6.0 },
  lic: { label: 'LIC / Insurance', category: 'Protection', color: '#e86c5d', defaultRate: 4.5 },
  real_estate: { label: 'Real Estate', category: 'Real assets', color: '#8b6fcb', defaultRate: 6.5 },
  fixed_deposit: { label: 'Fixed Deposits', category: 'Income', color: '#6c8f3d', defaultRate: 6.75 },
  bank_account: { label: 'Bank Accounts', category: 'Liquid', color: '#1f9bb4', defaultRate: 3.0 }
};

const portfolioColor = '#2f4858';
const projectionYears = [1, 3, 5, 10, 15, 20];
const retirementHorizonYears = 30;
const retirementCorpusMultiple = 40;
const retirementExpenseInflation = 6;
const minimumExpenseInflation = 0;
const maximumExpenseInflation = 15;
const minimumMonthlyExpense = 10000;
const maximumMonthlyExpense = 1000000;
const minimumMonthlySavings = 0;
const maximumMonthlySavings = 1000000;

const formatCurrency = (amount: number, digits = 0) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(Number.isFinite(amount) ? amount : 0);

const formatCompactCurrency = (amount: number) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  return formatCurrency(amount);
};

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const formatSignedPercent = (value: number) => `${value >= 0 ? '+' : ''}${formatPercent(value)}`;

interface ProjectionTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    payload?: {
      total?: number;
      growthPercent?: number;
    };
  }>;
}

export const ProjectionTooltip: React.FC<ProjectionTooltipProps> = ({ active, label, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className="analysis-chart-tooltip">
      <span>{label}</span>
      <div>
        <small>Projected net worth</small>
        <strong>{formatCurrency(Number(point.total || 0))}</strong>
      </div>
      <div>
        <small>Increase from today</small>
        <strong className="analysis-chart-tooltip__growth">
          {formatSignedPercent(Number(point.growthPercent || 0))}
        </strong>
      </div>
    </div>
  );
};

const annualizedFutureValue = (principal: number, annualRate: number, monthlyContribution: number, years: number) => {
  const rate = annualRate / 100;
  const principalGrowth = principal * Math.pow(1 + rate, years);
  if (!monthlyContribution || rate <= 0) {
    return principalGrowth + monthlyContribution * 12 * years;
  }
  const monthlyRate = Math.pow(1 + rate, 1 / 12) - 1;
  const months = years * 12;
  const contributionGrowth = monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
  return principalGrowth + contributionGrowth;
};

export const calculateRetirementProjection = (
  currentCorpus: number,
  annualGrowthRate: number,
  monthlyContribution: number,
  monthlyExpense: number,
  horizonYears = retirementHorizonYears,
  expenseInflation = retirementExpenseInflation,
  corpusMultiple = retirementCorpusMultiple
): RetirementProjectionResult => {
  const projectedCorpusAt = (years: number) =>
    annualizedFutureValue(currentCorpus, annualGrowthRate, monthlyContribution, years);
  const requiredCorpusAt = (years: number) =>
    monthlyExpense * 12 * corpusMultiple * Math.pow(1 + expenseInflation / 100, years);

  const data = Array.from({ length: horizonYears + 1 }, (_, year) => ({
    year,
    yearLabel: year === 0 ? 'Now' : `${year}Y`,
    projectedCorpus: Math.round(projectedCorpusAt(year)),
    requiredCorpus: Math.round(requiredCorpusAt(year))
  }));

  let crossingYears: number | null = null;
  let crossingCorpus: number | null = null;
  for (let month = 0; month <= horizonYears * 12; month += 1) {
    const years = month / 12;
    const projectedCorpus = projectedCorpusAt(years);
    const requiredCorpus = requiredCorpusAt(years);
    if (projectedCorpus >= requiredCorpus) {
      crossingYears = years;
      crossingCorpus = requiredCorpus;
      break;
    }
  }

  return { data, crossingYears, crossingCorpus };
};

interface RetirementTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    payload?: RetirementProjectionPoint;
  }>;
}

export const RetirementTooltip: React.FC<RetirementTooltipProps> = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className="analysis-chart-tooltip retirement-chart-tooltip">
      <span>{point.yearLabel}</span>
      <div>
        <small>Projected current corpus</small>
        <strong className="retirement-chart-tooltip__projected">{formatCurrency(point.projectedCorpus)}</strong>
      </div>
      <div>
        <small>Required retirement corpus</small>
        <strong className="retirement-chart-tooltip__required">{formatCurrency(point.requiredCorpus)}</strong>
      </div>
    </div>
  );
};

const averageMonthlyGrowth = (principal: number, annualRate: number) =>
  principal > 0 ? principal * annualRate / 100 / 12 : 0;

const numberFrom = (...values: unknown[]) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

type PlanningSection = 'current-position' | 'future-scenarios';
const getPlanningSection = (): PlanningSection =>
  window.location.hash === '#future-scenarios' ? 'future-scenarios' : 'current-position';

const FinancialAnalysis: React.FC = () => {
  const { token } = useAuth();
  const [assetsData, setAssetsData] = useState<Record<string, AssetSummary>>({});
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthlyExpense, setMonthlyExpense] = useState(75000);
  const [monthlySavingsInput, setMonthlySavingsInput] = useState('');
  const [expenseInflation, setExpenseInflation] = useState(retirementExpenseInflation);
  const [activeSection, setActiveSection] = useState<PlanningSection>(getPlanningSection);

  useEffect(() => {
    const syncSection = () => setActiveSection(getPlanningSection());
    window.addEventListener('hashchange', syncSection);
    return () => window.removeEventListener('hashchange', syncSection);
  }, []);

  const fetchAnalysisData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError('Please sign in to view financial analysis.');
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };
      const [assetsResponse, portfolioResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/assets/`, { headers }),
        axios.get(`${API_BASE_URL}/portfolio/summary/`, { headers })
      ]);
      setAssetsData(assetsResponse.data || {});
      setPortfolioSummary(portfolioResponse.data || null);
      setError('');
    } catch (err) {
      console.error('Failed to load financial analysis data:', err);
      setError('Failed to load financial analysis data.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalysisData();
  }, [fetchAnalysisData]);

  const wealthItems = useMemo<WealthItem[]>(() => {
    const portfolioValue = portfolioSummary?.current_value || 0;
    const items: WealthItem[] = [
      {
        key: 'portfolio',
        label: 'Portfolio',
        category: 'Market',
        value: portfolioValue,
        investment: portfolioSummary?.total_investment || portfolioValue,
        gain: portfolioSummary?.total_gain || 0,
        rate: 10.5,
        monthlyContribution: 0,
        color: portfolioColor
      }
    ];

    (Object.keys(assetConfig) as AssetKey[]).forEach((key) => {
      const asset = assetsData[key] || {};
      const config = assetConfig[key];
      const value = Number(asset.total_value || 0);
      const investment = Number(asset.investment || value);
      const rate = numberFrom(asset.xirr, asset.interest_rate, asset.metadata?.xirr, asset.metadata?.interest_rate, config.defaultRate);
      const monthlyContribution = numberFrom(asset.monthly_contribution, asset.metadata?.monthly_contribution);
      items.push({
        key,
        label: config.label,
        category: config.category,
        value,
        investment,
        gain: Number(asset.gain_loss || value - investment || 0),
        rate,
        monthlyContribution,
        color: config.color
      });
    });

    return items.filter((item) => item.value > 0);
  }, [assetsData, portfolioSummary]);

  const totalNetWorth = wealthItems.reduce((sum, item) => sum + item.value, 0);
  const totalInvestment = wealthItems.reduce((sum, item) => sum + item.investment, 0);
  const totalGain = wealthItems.reduce((sum, item) => sum + item.gain, 0);
  const weightedGrowthRate = totalNetWorth > 0
    ? wealthItems.reduce((sum, item) => sum + item.rate * item.value, 0) / totalNetWorth
    : 0;

  const distributionData = wealthItems
    .map((item) => ({
      ...item,
      weight: totalNetWorth > 0 ? (item.value / totalNetWorth) * 100 : 0,
      monthlyGrowth: averageMonthlyGrowth(item.value, item.rate)
    }))
    .sort((a, b) => b.value - a.value);

  const monthlyGrowthData = [...distributionData].sort((a, b) => b.monthlyGrowth - a.monthlyGrowth);
  const projectedMonthlyGrowth = monthlyGrowthData.reduce((sum, item) => sum + item.monthlyGrowth, 0);
  const projectedAnnualGrowth = projectedMonthlyGrowth * 12;
  const knownMonthlyAdditions = wealthItems.reduce((sum, item) => sum + item.monthlyContribution, 0);
  const monthlySavings = Math.min(
    Math.max(Number(monthlySavingsInput) || 0, minimumMonthlySavings),
    maximumMonthlySavings
  );
  const totalMonthlySavings = knownMonthlyAdditions + monthlySavings;
  const projectedMonthlyMovement = projectedMonthlyGrowth + knownMonthlyAdditions;
  const highestMonthlyGrowth = monthlyGrowthData[0]?.monthlyGrowth || 0;

  const categoryData = Object.values(
    wealthItems.reduce<Record<string, { category: string; value: number; color: string }>>((acc, item) => {
      if (!acc[item.category]) acc[item.category] = { category: item.category, value: 0, color: item.color };
      acc[item.category].value += item.value;
      return acc;
    }, {})
  ).sort((a, b) => b.value - a.value);

  const projectionData = projectionYears.map((year) => {
    const row: Record<string, number | string> = { year: `${year}Y` };
    let projectedTotal = 0;
    wealthItems.forEach((item) => {
      const projected = annualizedFutureValue(item.value, item.rate, item.monthlyContribution, year);
      row[item.label] = Math.round(projected);
      projectedTotal += projected;
    });
    row.total = Math.round(projectedTotal);
    row.growthPercent = totalNetWorth > 0
      ? ((projectedTotal - totalNetWorth) / totalNetWorth) * 100
      : 0;
    return row;
  });

  const largestHolding = distributionData[0];
  const topThreeWeight = distributionData.slice(0, 3).reduce((sum, item) => sum + item.weight, 0);
  const liquidValue = wealthItems
    .filter((item) => ['Bank Accounts', 'Fixed Deposits'].includes(item.label))
    .reduce((sum, item) => sum + item.value, 0);
  const liquidWeight = totalNetWorth > 0 ? (liquidValue / totalNetWorth) * 100 : 0;
  const retirementValue = wealthItems
    .filter((item) => item.category === 'Retirement' || item.label === 'LIC / Insurance')
    .reduce((sum, item) => sum + item.value, 0);
  const retirementWeight = totalNetWorth > 0 ? (retirementValue / totalNetWorth) * 100 : 0;
  const tenYearProjection = Number(projectionData.find((row) => row.year === '10Y')?.total || 0);
  const twentyYearProjection = Number(projectionData.find((row) => row.year === '20Y')?.total || 0);
  const tenYearNetWorthCagr = totalNetWorth > 0 && tenYearProjection > 0
    ? (Math.pow(tenYearProjection / totalNetWorth, 1 / 10) - 1) * 100
    : 0;
  const retirementProjection = calculateRetirementProjection(
    totalNetWorth,
    weightedGrowthRate,
    totalMonthlySavings,
    monthlyExpense,
    retirementHorizonYears,
    expenseInflation
  );
  const retirementYears = retirementProjection.crossingYears;
  const retirementWholeYears = retirementYears === null ? null : Math.floor(retirementYears);
  const retirementRemainingMonths = retirementYears === null
    ? null
    : Math.round((retirementYears - Math.floor(retirementYears)) * 12);
  const retirementTiming = retirementYears === null
    ? `Beyond ${retirementHorizonYears} years`
    : retirementYears === 0
      ? 'Ready now'
      : `${retirementWholeYears ? `${retirementWholeYears} year${retirementWholeYears === 1 ? '' : 's'}` : ''}${retirementWholeYears && retirementRemainingMonths ? ' ' : ''}${retirementRemainingMonths ? `${retirementRemainingMonths} month${retirementRemainingMonths === 1 ? '' : 's'}` : ''}`;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '420px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="financial-analysis-page">
      <section className="financial-analysis-hero mb-4">
        <div>
          <span className="financial-analysis-kicker">Wealth Analysis</span>
          <h1 className="financial-analysis-title">Financial analysis</h1>
          <p className="financial-analysis-subtitle">
            Distribution, concentration, liquidity, and forward growth estimates based on your latest available StockHunt asset and portfolio data.
          </p>
        </div>
        <div className="financial-analysis-hero__metric">
          <span>Total net worth</span>
          <strong>{formatCurrency(totalNetWorth)}</strong>
          <small>Weighted expected growth: {formatPercent(weightedGrowthRate)} yearly</small>
        </div>
      </section>

      <Tabs
        id="planning-sections"
        aria-label="Planning sections"
        className="analysis-section-tabs"
        activeKey={activeSection}
        transition={false}
        mountOnEnter
        onSelect={(section) => {
          if (section === 'current-position' || section === 'future-scenarios') {
            setActiveSection(section);
            window.location.hash = section;
          }
        }}
      >
        <Tab eventKey="current-position" hidden={activeSection !== 'current-position'} title={
          <span className="analysis-section-tab-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M11 3a9 9 0 1 0 10 10H11V3Z" />
              <path d="M15 3.5V9h5.5A9 9 0 0 0 15 3.5Z" />
            </svg>
            Current position
          </span>
        }>
          <h2 className="visually-hidden">Current position</h2>
          <div className="analysis-kpi-grid mb-4">
            <div>
              <Card className="analysis-kpi-card h-100">
                <Card.Body>
                  <span>Invested capital</span>
                  <strong>{formatCurrency(totalInvestment)}</strong>
                  <small>Across portfolio and assets</small>
                </Card.Body>
              </Card>
            </div>
            <div>
              <Card className="analysis-kpi-card h-100">
                <Card.Body>
                  <span>Unrealized gain</span>
                  <strong className={totalGain >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(totalGain)}</strong>
                  <small>{totalInvestment > 0 ? formatPercent((totalGain / totalInvestment) * 100) : '0.00%'} on invested value</small>
                </Card.Body>
              </Card>
            </div>
            <div>
              <Card className="analysis-kpi-card h-100">
                <Card.Body>
                  <span>Liquid allocation</span>
                  <strong>{formatPercent(liquidWeight)}</strong>
                  <small>{formatCurrency(liquidValue)} in cash and FDs</small>
                </Card.Body>
              </Card>
            </div>
          </div>
          <Row className="g-4 mb-4">
            <Col xxl={6}>
              <Card className="analysis-panel h-100">
                <Card.Body>
                  <div className="analysis-panel__header">
                    <div>
                      <span>Allocation</span>
                      <h2>Current wealth distribution</h2>
                    </div>
                    <Badge bg="light" text="dark">{wealthItems.length} buckets</Badge>
                  </div>
                  <div className="analysis-allocation-layout">
                    <div className="analysis-donut-wrap">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={distributionData}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={68}
                            outerRadius={104}
                            paddingAngle={2}
                            stroke="#ffffff"
                            strokeWidth={2}
                          >
                            {distributionData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(value: number) => formatCurrency(Number(value))} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="analysis-donut-center" aria-hidden="true">
                        <span>Total wealth</span>
                        <strong>{formatCompactCurrency(totalNetWorth)}</strong>
                      </div>
                    </div>
                    <div className="analysis-allocation-list">
                      {distributionData.map((item) => (
                        <div className="analysis-allocation-list__row" key={item.key}>
                          <div className="analysis-allocation-list__heading">
                            <span>
                              <i style={{ backgroundColor: item.color }} />
                              {item.label}
                            </span>
                            <strong>{formatPercent(item.weight)}</strong>
                          </div>
                          <div className="analysis-allocation-list__track" aria-hidden="true">
                            <span style={{ backgroundColor: item.color, width: `${Math.max(item.weight, 1.5)}%` }} />
                          </div>
                          <small>{formatCurrency(item.value)}</small>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={6}>
              <Card className="analysis-panel h-100">
                <Card.Body>
                  <div className="analysis-panel__header">
                    <div>
                      <span>Category mix</span>
                      <h2>Capital by purpose</h2>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryData} layout="vertical" margin={{ top: 8, right: 26, left: 24, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickFormatter={(value) => formatCompactCurrency(Number(value))} />
                      <YAxis type="category" dataKey="category" width={88} />
                      <Tooltip formatter={(value: number) => formatCurrency(Number(value))} />
                      <Bar dataKey="value" name="Current value" radius={[0, 8, 8, 0]}>
                        {categoryData.map((entry) => <Cell key={entry.category} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

          </Row>
        </Tab>
        <Tab eventKey="future-scenarios" hidden={activeSection !== 'future-scenarios'} title={
          <span className="analysis-section-tab-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m3 17 6-6 4 4 8-10M15 5h6v6" />
            </svg>
            Future scenarios
          </span>
        }>
          <h2 className="visually-hidden">Future scenarios</h2>
          <p className="workspace-status">Planning estimates use the assumptions shown below. Recorded values and projected growth are presented separately.</p>
          <div className="analysis-kpi-grid mb-4">
            <div>
              <Card className="analysis-kpi-card h-100">
                <Card.Body>
                  <span>Assumed avg net worth growth</span>
                  <strong>{formatPercent(tenYearNetWorthCagr)}</strong>
                  <small>10Y CAGR including known monthly additions; blended asset return is {formatPercent(weightedGrowthRate)} yearly.</small>
                </Card.Body>
              </Card>
            </div>
            <div>
              <Card className="analysis-kpi-card h-100">
                <Card.Body>
                  <span>10 year base case</span>
                  <strong>{formatCompactCurrency(tenYearProjection)}</strong>
                  <small>{formatCompactCurrency(Math.max(tenYearProjection - totalNetWorth, 0))} projected growth</small>
                </Card.Body>
              </Card>
            </div>
          </div>
          <Card className="analysis-panel analysis-income-panel mb-4">
            <Card.Body>
              <div className="analysis-panel__header">
                <div>
                  <span>Monthly income projection</span>
                  <h2>Projected monthly wealth growth</h2>
                </div>
                <Badge bg="light" text="dark">Planning estimate</Badge>
              </div>
              <div className="analysis-income-layout">
                <div className="analysis-income-primary">
                  <span>Income-equivalent growth</span>
                  <strong>
                    {formatCurrency(projectedMonthlyGrowth)}
                    <small>/ month</small>
                  </strong>
                  <p>
                    Average monthly increase implied by current asset values and their displayed annual growth assumptions.
                    It represents appreciation and interest, not guaranteed cash paid into your bank account.
                  </p>
                  <div className="analysis-income-rate">{formatPercent(weightedGrowthRate)} blended annual return</div>
                </div>
                <div className="analysis-income-summary">
                  <div>
                    <span>Annual growth equivalent</span>
                    <strong>{formatCurrency(projectedAnnualGrowth)}</strong>
                    <small>Before new contributions</small>
                  </div>
                  <div>
                    <span>Known monthly additions</span>
                    <strong>{formatCurrency(knownMonthlyAdditions)}</strong>
                    <small>Contributions recorded in assets</small>
                  </div>
                  <div>
                    <span>Projected net-worth movement</span>
                    <strong>{formatCurrency(projectedMonthlyMovement)}</strong>
                    <small>Growth plus known additions</small>
                  </div>
                </div>
              </div>
              <div className="analysis-growth-stream" aria-label="Projected monthly growth by asset">
                {monthlyGrowthData.map((item) => (
                  <div className="analysis-growth-stream__row" key={item.key}>
                    <div className="analysis-growth-stream__label">
                      <span className="analysis-color-dot" style={{ backgroundColor: item.color }} />
                      <span>{item.label}</span>
                    </div>
                    <div className="analysis-growth-stream__track" aria-hidden="true">
                      <span
                        style={{
                          backgroundColor: item.color,
                          width: `${highestMonthlyGrowth > 0 ? Math.max((item.monthlyGrowth / highestMonthlyGrowth) * 100, 2) : 0}%`
                        }}
                      />
                    </div>
                    <strong>{formatCurrency(item.monthlyGrowth)}</strong>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          <Card className="analysis-panel retirement-readiness-panel mb-4">
            <Card.Body>
              <div className="analysis-panel__header">
                <div>
                  <span>Retirement readiness</span>
                  <h2>How early can I retire?</h2>
                </div>
                <Badge bg="light" text="dark">40× annual expenses</Badge>
              </div>
              <div className="retirement-readiness-layout">
                <div className="retirement-readiness-controls">
                  <div className="retirement-readiness-result">
                    <span>Estimated crossover</span>
                    <strong>{retirementTiming}</strong>
                    <small>
                      {retirementProjection.crossingCorpus === null
                        ? 'The projected corpus does not reach the requirement within the displayed horizon.'
                        : `${formatCompactCurrency(retirementProjection.crossingCorpus)} required at crossover`}
                    </small>
                  </div>

                  <div className="retirement-expense-control">
                    <div className="retirement-expense-control__heading">
                      <label htmlFor="monthly-retirement-expense">Monthly household expense</label>
                      <strong>{formatCurrency(monthlyExpense)}</strong>
                    </div>
                    <input
                      id="monthly-retirement-expense"
                      type="range"
                      min={minimumMonthlyExpense}
                      max={maximumMonthlyExpense}
                      step={5000}
                      value={Math.min(Math.max(monthlyExpense, minimumMonthlyExpense), maximumMonthlyExpense)}
                      onChange={(event) => setMonthlyExpense(Number(event.target.value))}
                    />
                    <div className="retirement-expense-input">
                      <span aria-hidden="true">₹</span>
                      <input
                        type="number"
                        aria-label="Monthly expense amount"
                        min={minimumMonthlyExpense}
                        max={maximumMonthlyExpense}
                        step={1000}
                        value={monthlyExpense}
                        onChange={(event) => setMonthlyExpense(Math.max(Number(event.target.value) || 0, 0))}
                        onBlur={() => setMonthlyExpense(Math.min(Math.max(monthlyExpense, minimumMonthlyExpense), maximumMonthlyExpense))}
                      />
                      <small>per month</small>
                    </div>
                  </div>

                  <div className="retirement-savings-control">
                    <div className="retirement-expense-control__heading">
                      <label htmlFor="monthly-retirement-savings">Monthly saving contribution</label>
                      <strong>{formatCurrency(monthlySavings)}</strong>
                    </div>
                    <div className="retirement-expense-input">
                      <span aria-hidden="true">₹</span>
                      <input
                        id="monthly-retirement-savings"
                        type="number"
                        aria-label="Monthly saving contribution"
                        min={minimumMonthlySavings}
                        max={maximumMonthlySavings}
                        step={1000}
                        placeholder="0"
                        value={monthlySavingsInput}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          if (!/^\d*$/.test(nextValue)) return;
                          if (!nextValue) {
                            setMonthlySavingsInput('');
                            return;
                          }
                          setMonthlySavingsInput(String(Math.min(Number(nextValue), maximumMonthlySavings)));
                        }}
                        onBlur={() => {
                          const normalizedValue = Math.min(Number(monthlySavingsInput) || 0, maximumMonthlySavings);
                          setMonthlySavingsInput(normalizedValue > 0 ? String(normalizedValue) : '');
                        }}
                      />
                      <small>per month</small>
                    </div>
                    <small className="retirement-control-note">
                      Added to known monthly contributions and compounded using the current yearly growth of {formatPercent(weightedGrowthRate)}.
                    </small>
                  </div>

                  <div className="retirement-inflation-control">
                    <div className="retirement-expense-control__heading">
                      <label htmlFor="retirement-expense-inflation">Annual expense inflation</label>
                      <strong>{formatPercent(expenseInflation)}</strong>
                    </div>
                    <input
                      id="retirement-expense-inflation"
                      type="range"
                      min={minimumExpenseInflation}
                      max={maximumExpenseInflation}
                      step={0.5}
                      value={expenseInflation}
                      onChange={(event) => setExpenseInflation(Number(event.target.value))}
                    />
                    <div className="retirement-inflation-control__scale" aria-hidden="true">
                      <span>{formatPercent(minimumExpenseInflation)}</span>
                      <span>{formatPercent(maximumExpenseInflation)}</span>
                    </div>
                  </div>

                  <div className="retirement-assumptions" aria-label="Retirement projection assumptions">
                    <div>
                      <span>Corpus rule</span>
                      <strong>{retirementCorpusMultiple}×</strong>
                    </div>
                    <div>
                      <span>Expense inflation</span>
                      <strong>{formatPercent(expenseInflation)}</strong>
                    </div>
                    <div>
                      <span>Current yearly growth</span>
                      <strong>{formatPercent(weightedGrowthRate)}</strong>
                    </div>
                    <div>
                      <span>Total monthly saving</span>
                      <strong>{formatCurrency(totalMonthlySavings)}</strong>
                    </div>
                  </div>
                </div>

                <div className="retirement-readiness-chart">
                  <div className="retirement-chart-legend" aria-label="Retirement chart legend">
                    <span><i className="retirement-chart-legend__projected" />Projected current corpus</span>
                    <span><i className="retirement-chart-legend__required" />Required retirement corpus</span>
                  </div>
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart data={retirementProjection.data} margin={{ top: 16, right: 24, left: 12, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="year"
                        ticks={[0, 5, 10, 15, 20, 25, 30]}
                        tickFormatter={(year) => year === 0 ? 'Now' : `${year}Y`}
                        interval="preserveStartEnd"
                        minTickGap={18}
                      />
                      <YAxis tickFormatter={(value) => formatCompactCurrency(Number(value))} width={82} />
                      <Tooltip content={<RetirementTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="projectedCorpus"
                        name="Projected current corpus"
                        stroke="#16825d"
                        strokeWidth={3}
                        dot={false}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="requiredCorpus"
                        name="Required retirement corpus"
                        stroke="#d33f58"
                        strokeWidth={3}
                        strokeDasharray="7 5"
                        dot={false}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className="analysis-note mb-0">
                Required corpus starts at 40 times the annual expense entered and rises with {formatPercent(expenseInflation)} yearly expense inflation. The current corpus projection uses today's net worth,
                the live blended yearly growth assumption, known monthly additions, and the additional monthly saving entered above. This is a planning estimate, not a guaranteed retirement date.
              </p>
            </Card.Body>
          </Card>


          <Row className="g-4 mb-4">
            <Col xxl={6}>
              <Card className="analysis-panel h-100">
                <Card.Body>
                  <div className="analysis-panel__header">
                    <div>
                      <span>Projection</span>
                      <h2>Expected future asset growth</h2>
                    </div>
                    <Badge bg="light" text="dark">Base case</Badge>
                  </div>
                  <div className="analysis-chart">
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={projectionData} margin={{ top: 12, right: 24, left: 10, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(value) => formatCompactCurrency(Number(value))} width={78} />
                        <Tooltip content={<ProjectionTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="total" name="Projected net worth" stroke="#2f4858" strokeWidth={3} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="analysis-note mb-0">
                    Projection uses each asset's available XIRR or interest rate, then falls back to conservative long-term assumptions.
                    Percentage increase is measured from today's net worth and includes known monthly contributions where the asset record provides them.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={6}>
              <Card className="analysis-panel h-100">
                <Card.Body>
                  <div className="analysis-panel__header">
                    <div>
                      <span>Readout</span>
                      <h2>Detailed analysis</h2>
                    </div>
                  </div>
                  <div className="analysis-readout-list">
                    <div>
                      <strong>Average growth assumption</strong>
                      <p>
                        The assumed average net worth growth is {formatPercent(tenYearNetWorthCagr)} over the next 10 years,
                        including known monthly contributions. Pure blended asset return is {formatPercent(weightedGrowthRate)} based on current weights.
                      </p>
                    </div>
                    <div>
                      <strong>Concentration</strong>
                      <p>
                        {largestHolding
                          ? `${largestHolding.label} is the largest bucket at ${formatPercent(largestHolding.weight)} of net worth. Top three buckets hold ${formatPercent(topThreeWeight)}.`
                          : 'Add assets to measure concentration.'}
                      </p>
                    </div>
                    <div>
                      <strong>Liquidity</strong>
                      <p>
                        Cash and fixed deposits are {formatPercent(liquidWeight)} of net worth. This is the immediately flexible reserve before selling long-term assets.
                      </p>
                    </div>
                    <div>
                      <strong>Retirement base</strong>
                      <p>
                        Retirement and insurance-linked assets are {formatPercent(retirementWeight)} of net worth, giving the portfolio a long-duration stability layer.
                      </p>
                    </div>
                    <div>
                      <strong>Long-term path</strong>
                      <p>
                        The 20-year base case reaches {formatCompactCurrency(twentyYearProjection)}, assuming the current mix and known contributions continue.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

          </Row>

          <Card className="analysis-panel mb-4">
            <Card.Body>
              <div className="analysis-panel__header">
                <div>
                  <span>Asset table</span>
                  <h2>Weights, returns, and assumptions</h2>
                </div>
              </div>
              <Table responsive hover className="analysis-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Category</th>
                    <th className="text-end">Current value</th>
                    <th className="text-end">Weight</th>
                    <th className="text-end">Gain/Loss</th>
                    <th className="text-end">Assumed growth</th>
                    <th className="text-end">Monthly growth</th>
                    <th className="text-end">Monthly add</th>
                    <th className="text-end">10Y estimate</th>
                  </tr>
                </thead>
                <tbody>
                  {distributionData.map((item) => (
                    <tr key={item.key}>
                      <td>
                        <span className="analysis-color-dot" style={{ backgroundColor: item.color }} />
                        {item.label}
                      </td>
                      <td>{item.category}</td>
                      <td className="text-end">{formatCurrency(item.value)}</td>
                      <td className="text-end">{formatPercent(item.weight)}</td>
                      <td className={`text-end ${item.gain >= 0 ? 'text-success' : 'text-danger'}`}>{formatCurrency(item.gain)}</td>
                      <td className="text-end">{formatPercent(item.rate)}</td>
                      <td className="text-end">{formatCurrency(item.monthlyGrowth)}</td>
                      <td className="text-end">{formatCurrency(item.monthlyContribution)}</td>
                      <td className="text-end">{formatCurrency(annualizedFutureValue(item.value, item.rate, item.monthlyContribution, 10))}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default FinancialAnalysis;
